import { reactive } from 'vue'
import dayjs from 'dayjs'

import { usePlanningStore } from '@/stores/planning.store'
import { usePortalStore } from '@/stores/portal.store'
import { eventTypeEnum } from '@/stores/schedule.store'

// Data source of WarehouseAvailabilityTimeline: the `warehouse-stocks/timeline/` snapshot of the
// loaded window, turned into a per-day series of physical, booked and projected quantities.
export function useWarehouseAvailabilityData() {
  const planningStore = usePlanningStore()
  const portalStore = usePortalStore()
  const { OK, BELOW_MIN, SHORTAGE } = planningStore.warehouseAvailabilityStatusEnum
  const { LOAD, UNLOAD } = planningStore.warehouseMovementTypeEnum
  const { ALLOCATION, ABSENCE } = eventTypeEnum

  const state = reactive({
    warehouses: [],
    projects: new Map(),
    items: [],
    stock: {},
    movements: [],
    events: [],
    minStocks: [],
    rangeStart: dayjs().startOf('day'),
    rangeDays: 0,
    loading: false,
  })

  const dayIndex = (value) => dayjs(value).startOf('day').diff(state.rangeStart, 'day')
  const clampIndex = (index) => Math.min(Math.max(index, 0), state.rangeDays - 1)

  const loadAsync = async(rangeStart, rangeDays) => {
    state.loading = true
    try {
      const { data } = await planningStore.fetchWarehouseAvailabilityTimelineAsync(
        rangeStart.toISOString(),
        rangeStart.add(rangeDays, 'day').toISOString(),
      )
      state.warehouses = data.warehouses
      state.projects = new Map(data.projects.map(project => [project.id, project]))
      state.items = data.items
      state.stock = data.stock
      state.movements = data.movements
      state.events = data.events
      state.minStocks = data.min_stocks
      state.rangeStart = rangeStart
      state.rangeDays = rangeDays
    } finally {
      state.loading = false
    }
  }

  const reloadAsync = () => loadAsync(state.rangeStart, state.rangeDays)

  const setMinStockAsync = async(serviceId, warehouseId, quantity) => {
    state.minStocks = [
      ...state.minStocks.filter(m => m.service_id !== serviceId || m.warehouse_id !== warehouseId),
      ...(quantity === null ? [] : [{ service_id: serviceId, warehouse_id: warehouseId, quantity }]),
    ]
    await planningStore.setWarehouseMinStockAsync(serviceId, warehouseId, quantity)
  }

  const dailyStock = (intervals) => Array.from({ length: state.rangeDays }, (_, index) => {
    const dayStart = state.rangeStart.add(index, 'day')
    const dayEnd = dayStart.add(1, 'day')
    const quantities = intervals
      .filter(interval => dayjs(interval.from).isBefore(dayEnd) && dayjs(interval.to).isAfter(dayStart))
      .map(interval => interval.quantity)
    return quantities.length ? Math.min(...quantities) : 0
  })

  // Daily allocation events of one project merged back into the booked runs.
  const runsOf = (events) => {
    const runs = []
    for (const event of [...events].sort((a, b) => dayjs(a.start).diff(b.start))) {
      const startIndex = dayIndex(event.start)
      const endIndex = dayIndex(event.end)
      const run = runs.findLast(r => r.project_id === event.project_id && r.confirmed === event.confirmed &&
        r.type === event.type && r.quantity === event.quantity && startIndex <= r.endIndex + 1)
      if (run) {
        run.endIndex = Math.max(run.endIndex, endIndex)
        run.end = event.end
      } else {
        runs.push({ ...event, startIndex, endIndex })
      }
    }
    return runs
  }

  // An unconfirmed allocation has no movements yet: these show where it would check out and return.
  const plannedMovements = (events) => runsOf(events).flatMap(run => [UNLOAD, LOAD].map(movementType => ({
    id: `planned-${run.id}-${movementType}`,
    service_id: run.service_id,
    project_id: run.project_id,
    movement_type: movementType,
    quantity: run.quantity,
    scheduled_at: movementType === UNLOAD ? run.start : run.end,
    planned: true,
  })))

  const episodesOf = (days, status) => {
    const episodes = []
    let current = null
    days.forEach((day, index) => {
      if (day.status === status) {
        current ??= { startIndex: index, worst: day.projected, real: false }
        current.endIndex = index
        current.worst = Math.min(current.worst, day.projected)
        current.real ||= day.physical < 0
        return
      }
      if (current) episodes.push(current)
      current = null
    })
    if (current) episodes.push(current)
    return episodes.map(e => ({ ...e, status, start: days[e.startIndex].key, end: days[e.endIndex].key }))
  }

  // A warehouse below its own minimum flags the item even when the total looks fine.
  const dayStatus = (projected, warehouseDays, minByWarehouse, warehouseId) => {
    if (projected < 0) return SHORTAGE
    const scoped = warehouseDays.filter(w => minByWarehouse[w.id] !== undefined)
    const totalMin = scoped.reduce((sum, w) => sum + minByWarehouse[w.id], 0)
    const belowTotal = scoped.length > 0 && warehouseId === null && projected < totalMin
    return belowTotal || scoped.some(w => w.physical < minByWarehouse[w.id]) ? BELOW_MIN : OK
  }

  const build = (warehouseId) => {
    const stockEntries = Object.entries(state.stock).map(([key, intervals]) => {
      const [stockWarehouseId, serviceId] = key.split('_').map(Number)
      return { warehouseId: stockWarehouseId, serviceId, days: dailyStock(intervals) }
    })
    // Unconfirmed allocations have no warehouse yet, so they weigh only on the all-warehouses view.
    const countPlanned = warehouseId === null
    // The past is drawn but never flagged: a shortage there can no longer be acted upon.
    const todayIndex = dayIndex(dayjs())
    const dayKeys = Array.from({ length: state.rangeDays }, (_, index) => state.rangeStart.add(index, 'day').format('YYYY-MM-DD'))

    return state.items.flatMap((item) => {
      const minByWarehouse = Object.fromEntries(state.minStocks
        .filter(m => m.service_id === item.id)
        .map(m => [m.warehouse_id, m.quantity]))
      const series = state.warehouses
        .map((warehouse) => {
          const stock = stockEntries.find(s => s.serviceId === item.id && s.warehouseId === warehouse.id)
          if (!stock && minByWarehouse[warehouse.id] === undefined) return null
          return { id: warehouse.id, name: warehouse.name, days: stock?.days ?? new Array(state.rangeDays).fill(0) }
        })
        .filter(Boolean)
      const inScope = series.filter(w => warehouseId === null || w.id === warehouseId)
      if (!inScope.length && warehouseId !== null) return []
      const others = series.filter(w => warehouseId !== null && w.id !== warehouseId)
      const ownEvents = state.events.filter(e => e.service_id === item.id)
      const unconfirmed = ownEvents.filter(e => e.type === ALLOCATION && !e.confirmed)
      const planned = new Array(state.rangeDays).fill(0)
      for (const event of countPlanned ? unconfirmed : []) {
        for (let index = clampIndex(dayIndex(event.start)); index <= clampIndex(dayIndex(event.end)); index++) planned[index] += event.quantity
      }
      const scopedMins = Object.entries(minByWarehouse).filter(([id]) => warehouseId === null || Number(id) === warehouseId)
      const minStock = scopedMins.length ? scopedMins.reduce((sum, [, value]) => sum + value, 0) : null
      const days = dayKeys.map((key, index) => {
        const warehouseDays = inScope.map(w => ({ id: w.id, name: w.name, physical: w.days[index] }))
        const physical = warehouseDays.reduce((sum, w) => sum + w.physical, 0)
        const projected = physical - planned[index]
        return {
          key,
          index,
          physical,
          planned: planned[index],
          projected,
          status: index < todayIndex ? OK : dayStatus(projected, warehouseDays, minByWarehouse, warehouseId),
          belowMinWarehouses: index < todayIndex ? [] : warehouseDays.filter(w => minByWarehouse[w.id] !== undefined && w.physical < minByWarehouse[w.id]).map(w => w.name),
          elsewhere: others.reduce((sum, w) => sum + Math.max(w.days[index], 0), 0),
          byWarehouse: series.map(w => ({ id: w.id, name: w.name, physical: w.days[index] })),
        }
      })
      return {
        ...item,
        isStaff: item.type === portalStore.serviceTypeEnum.STAFF,
        minStock,
        minByWarehouse,
        days,
        shortages: episodesOf(days, SHORTAGE),
        belowMin: episodesOf(days, BELOW_MIN),
        commitments: runsOf(ownEvents.filter(e => e.type === ALLOCATION || e.type === ABSENCE))
          .map(run => ({ ...run, project: state.projects.get(run.project_id) ?? null })),
        movements: [
          ...state.movements.filter(m => m.service_id === item.id && (warehouseId === null || m.warehouse_id === warehouseId)),
          ...(countPlanned ? plannedMovements(unconfirmed) : []),
        ].map(m => ({ ...m, planned: !!m.planned, project: state.projects.get(m.project_id) ?? null })),
        scale: Math.max(item.owned, minStock ?? 0, ...days.map(day => Math.max(day.physical, day.projected)), 1),
      }
    })
  }

  return { state, dayIndex, loadAsync, reloadAsync, setMinStockAsync, build }
}
